/**
 * PluginRegistry.ts - Self-registering plugin system
 * 
 * PURPOSE:
 * - Plugins register themselves on import
 * - No hardcoded plugin lists
 * - Scalable and maintainable
 * - Apps can query available plugins
 * 
 * ARCHITECTURE:
 * - Plugins call `registerPlugin()` in their index.ts
 * - Registry maintains metadata and loaders
 * - Apps use `getPlugin()` or `getAvailablePlugins()`
 */

import type { Plugin } from './PluggrTypes';

/**
 * Plugin metadata for registration
 */
export interface PluginMetadata {
  id: string;
  name: string;
  description: string;
  category: string;
  version?: string;
  author?: string;
  tags?: string[];
  
  // Component information (if plugin has UI)
  hasComponent?: boolean;
  componentPackage?: string;  // e.g., '@opcentral/organizr'
  componentExport?: string;    // e.g., 'ListManagerView'
  
  // Plugin loader
  loader: () => Promise<Plugin>;
}

/**
 * Registered plugin entry
 */
interface RegisteredPlugin {
  metadata: PluginMetadata;
  loaded: boolean;
  instance?: Plugin;
}

/**
 * Plugin Registry - Singleton
 */
class PluginRegistry {
  private plugins: Map<string, RegisteredPlugin> = new Map();
  private initialized: boolean = false;

  /**
   * Register a plugin
   * Called by plugins in their index.ts files
   */
  register(metadata: PluginMetadata): void {
    if (this.plugins.has(metadata.id)) {
      console.warn(`[PluginRegistry] Plugin already registered: ${metadata.id}`);
      return;
    }

    this.plugins.set(metadata.id, {
      metadata,
      loaded: false
    });

    console.log(`[PluginRegistry] Registered plugin: ${metadata.id}`);
  }

  /**
   * Get all available plugins
   */
  getAvailable(): PluginMetadata[] {
    return Array.from(this.plugins.values()).map(p => p.metadata);
  }

  /**
   * Get plugins by category
   */
  getByCategory(category: string): PluginMetadata[] {
    return this.getAvailable().filter(p => p.category === category);
  }

  /**
   * Get plugins with components
   */
  getWithComponents(): PluginMetadata[] {
    return this.getAvailable().filter(p => p.hasComponent);
  }

  /**
   * Check if plugin exists
   */
  has(pluginId: string): boolean {
    return this.plugins.has(pluginId);
  }

  /**
   * Get plugin metadata
   */
  getMetadata(pluginId: string): PluginMetadata | undefined {
    return this.plugins.get(pluginId)?.metadata;
  }

  /**
   * Load and instantiate a plugin
   */
  async load(pluginId: string): Promise<Plugin> {
    const entry = this.plugins.get(pluginId);
    
    if (!entry) {
      const available = Array.from(this.plugins.keys()).join(', ');
      throw new Error(`Plugin not found: ${pluginId}. Available: ${available || 'none'}`);
    }

    // Return cached instance if already loaded
    if (entry.loaded && entry.instance) {
      return entry.instance;
    }

    // Load the plugin
    try {
      const plugin = await entry.metadata.loader();
      entry.instance = plugin;
      entry.loaded = true;
      console.log(`[PluginRegistry] Loaded plugin: ${pluginId}`);
      return plugin;
    } catch (error) {
      console.error(`[PluginRegistry] Failed to load plugin ${pluginId}:`, error);
      throw error;
    }
  }

  /**
   * Unload a plugin (clear cache)
   */
  unload(pluginId: string): void {
    const entry = this.plugins.get(pluginId);
    if (entry) {
      entry.loaded = false;
      entry.instance = undefined;
      console.log(`[PluginRegistry] Unloaded plugin: ${pluginId}`);
    }
  }

  /**
   * Mark registry as initialized
   */
  markInitialized(): void {
    this.initialized = true;
    console.log(`[PluginRegistry] Initialized with ${this.plugins.size} plugins`);
  }

  /**
   * Check if registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get plugin count
   */
  count(): number {
    return this.plugins.size;
  }

  /**
   * Clear all plugins (for testing)
   */
  clear(): void {
    this.plugins.clear();
    this.initialized = false;
  }
}

// Singleton instance
const registry = new PluginRegistry();

/**
 * Register a plugin (called by plugins)
 */
export function registerPlugin(metadata: PluginMetadata): void {
  registry.register(metadata);
}

/**
 * Get the plugin registry instance
 */
export function getPluginRegistry(): PluginRegistry {
  return registry;
}

/**
 * Get all available plugins
 */
export function getAvailablePlugins(): PluginMetadata[] {
  return registry.getAvailable();
}

/**
 * Load a plugin by ID
 */
export async function loadPlugin(pluginId: string): Promise<Plugin> {
  return registry.load(pluginId);
}

/**
 * Check if plugin exists
 */
export function hasPlugin(pluginId: string): boolean {
  return registry.has(pluginId);
}

/**
 * Get plugin metadata
 */
export function getPluginMetadata(pluginId: string): PluginMetadata | undefined {
  return registry.getMetadata(pluginId);
}

export default registry;
